import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/services/auth.service';
import { StaffStateService } from '../../../auth/services/staff-state.service';

/**
 * Displays the application header.
 * Provides controls for toggling the sidebar, opening and closing
 * the staff profile menu, displaying the authenticated user's initials,
 * and logging out of the application.
 * Uses Angular's `OnPush` change detection strategy for improved
 * rendering performance.
 */
@Component({
  selector: 'zba-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  /**
   * Authentication service used to manage the current user's
   * authentication state and perform logout operations.
   */
  private readonly authService = inject(AuthService);

  /**
   * Router used to navigate the user to the sign-in page after logout.
   */
  private readonly router = inject(Router);

  /**
   * Reference to the component's host DOM element.
   * Used to determine whether a document click occurred outside
   * the header component.
   */
  private readonly elementRef = inject(
    ElementRef<HTMLElement>,
  );

  /**
   * Service used to access the currently authenticated staff member.
   */
  private readonly staffStateService = inject(StaffStateService);

  /**
   * Indicates whether the sidebar is currently collapsed.
   * This input is controlled by the parent component.
   */
  readonly sidebarCollapsed = input<boolean>(false);

  /**
   * Emits the updated sidebar collapsed state when the user
   * toggles the sidebar.
   */
  readonly sidebarCollapsedChange = output<boolean>();

  /**
   * Indicates whether the profile menu is currently visible.
   */
  isProfileMenuOpen = false;

  /**
   * Computes the initials displayed for the authenticated staff member.
   * The initials are derived from the staff member's first and last name.
   * When a full name is unavailable, the email address is used as a fallback.
   */
  readonly profileInitials = computed(() =>this.getProfileInitials(),);

  /**
   * Toggles the sidebar between its collapsed and expanded states.
   * Emits the opposite of the current sidebar state to the parent component.
   */
  toggleSidebar(): void {
    this.sidebarCollapsedChange.emit(
      !this.sidebarCollapsed(),
    );
  }

  /**
   * Toggles the visibility of the profile menu.
   */
  toggleProfileMenu(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  /**
   * Closes the profile menu.
   */
  closeProfileMenu(): void {
    this.isProfileMenuOpen = false;
  }

  /**
   * Logs the current user out of the application.
   * Closes the profile menu, clears the authentication state,
   * and navigates the user to the sign-in page.
   */
  logout(): void {
    this.closeProfileMenu();
    this.authService.logout();
    this.router.navigate(['/signin']);
  }

  /**
   * Handles clicks anywhere in the document.
   * Closes the profile menu when the user clicks outside
   * the header component.
   * @param event Mouse event generated by the document click.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isProfileMenuOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeProfileMenu();
    }
  }

  /**
   * Generates the initials used for the authenticated user's profile.
   * The method first attempts to use the first and last name.
   * If only one name is available, it uses the first two valid
   * alphabetic characters from that name. When no name is available,
   * the username portion of the email address is used as a fallback.
   * @returns Uppercase profile initials, or `U` when no staff
   * information is available.
   */
  private getProfileInitials(): string {
    const staff = this.staffStateService.staff();
    if (!staff) {
      return 'U';
    }

    const nameParts = [staff.firstName, staff.lastName]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map(part => part.trim());

    /**
     * Uses the first character of the first and last names
     * when both names are available.
     */
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }

    const singleName = nameParts[0] || staff.email?.split('@')[0] || 'U';
    return singleName.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || 'U';
  }
}